import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import SearchIcon from '@mui/icons-material/Search';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { Account } from '../../../types';
import { Autocomplete, TextField } from '@mui/material';
import { INPUT_STYLE } from '../../../utils/styleHelpers';
import { searchAccounts } from '../../../Api/Admin/Accounts/searchAccounts';
import { getUser, hasAllAccountScope } from '../../../store/User';
import { getRecentAccounts, pushRecentAccount, PM_CONFIG_RECENT_ACCOUNTS, RecentAccountsStore } from './recentAccounts';

const RECENT_GROUP = 'RECENT';

export interface AccountSelectionFormProps {
  accounts?: Account[];
  selectedAccount: Account | null;
  setSelectedAccount: (acct: Account | undefined) => void;
  showHeader?: boolean;
  size?: 'small' | 'medium' | undefined;
  styles?: string;
  disabled?: boolean;
  showLabel?: boolean;
  inputStyles?: string;
  styleOverride?: object;
  // Open the dropdown on focus and, before any typing, list the accounts the user recently
  // selected here (a small persisted list) instead of the full account directory.
  openOnFocus?: boolean;
  // Which persisted recent list this picker reads and writes; surfaces keep their own.
  recentAccountsStore?: RecentAccountsStore;
  // Split the dropdown into RECENT / SEARCH RESULTS sections behind a search icon.
  searchLayout?: boolean;
  // Denominator for the SEARCH RESULTS count — how many accounts this user can reach.
  totalAccountCount?: number;
  label?: string;
}

const sortAccountsByName = (accounts: Account[]): Account[] =>
  [...accounts].sort((a, b) => {
    const nameA = a.name?.toLowerCase();
    const nameB = b.name?.toLowerCase();
    if (!nameA) return 1;
    if (!nameB) return -1;
    return nameA.localeCompare(nameB);
  });

export const AccountSelectionForm = ({
  accounts = [],
  selectedAccount,
  setSelectedAccount,
  showHeader = false,
  size = 'small',
  styles,
  disabled = false,
  showLabel = true,
  inputStyles = '',
  styleOverride,
  openOnFocus = false,
  recentAccountsStore = PM_CONFIG_RECENT_ACCOUNTS,
  searchLayout = false,
  totalAccountCount,
  label = 'Account'
}: AccountSelectionFormProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedAccounts, setSearchedAccounts] = useState<Account[]>(sortAccountsByName(accounts));
  const [recents, setRecents] = useState<Account[]>([]);
  const [matchCount, setMatchCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const searchSeq = useRef(0);
  const user = getUser();
  const canSearchDirectory = hasAllAccountScope(user);
  // Only the directory-search roles get the sectioned layout. Account-scoped roles are handed a
  // hydrated book through `accounts`, which the sections would hide until they typed something.
  const sectioned = searchLayout && canSearchDirectory;

  // The current selection belongs with the recents — it is by definition the most recent one — and
  // keeping it out of the results tail stops MUI opening the listbox scrolled down to it.
  const displayedRecents = useMemo(() => {
    if (!sectioned || !selectedAccount) return recents;
    return recents.some((acct) => acct.id === selectedAccount.id) ? recents : [selectedAccount, ...recents];
  }, [sectioned, recents, selectedAccount]);

  const recentIds = useMemo(() => new Set(displayedRecents.map((acct) => acct.id)), [displayedRecents]);
  const searchResults = useMemo(
    () => (sectioned ? searchedAccounts.filter((acct) => !recentIds.has(acct.id)) : searchedAccounts),
    [sectioned, searchedAccounts, recentIds]
  );

  const options = useMemo(() => {
    if (sectioned) return [...displayedRecents, ...searchResults];
    if (!selectedAccount || searchResults.some((acct) => acct.id === selectedAccount.id)) return searchResults;
    // Keep the controlled value present in options so MUI does not warn about an unknown value.
    return [selectedAccount, ...searchResults];
  }, [sectioned, displayedRecents, searchResults, selectedAccount]);

  // The count is every match the API found, not the capped page below it — a broad term matching
  // hundreds must not read as "20". Undefined total means unknown (the field is optional); a real
  // 0 still gets counted.
  const searchResultsGroup =
    totalAccountCount === undefined
      ? 'SEARCH RESULTS'
      : `SEARCH RESULTS (${matchCount.toLocaleString()} of ${totalAccountCount.toLocaleString()})`;

  // null keeps the Autocomplete controlled when empty; the cast satisfies disableClearable's non-null value typing
  const selectedValue = (selectedAccount ?? null) as Account;

  const handleSearch = useCallback(
    debounce(async (query: string) => {
      const seq = searchSeq.current;
      setSearching(true);
      const { data } = await searchAccounts(query);
      if (seq !== searchSeq.current) return;
      setSearching(false);
      // Kept in the order the API returned: it ranks by where the term hits the name, so
      // re-sorting alphabetically here would throw the ranking away.
      setSearchedAccounts(data?.data || []);
      setMatchCount(data?.totalMatches ?? 0);
    }, 250),
    []
  );

  useEffect(() => {
    // The sectioned list holds search results only — recents cover the untyped state, so seeding it
    // from the accounts prop would show a full list before the user has searched for anything.
    if (sectioned) return;
    if (accounts.length > 0) setSearchedAccounts(sortAccountsByName(accounts));
  }, [accounts, sectioned]);

  // Re-read when the user resolves: on the first render after a reload `getUser()` can still be
  // empty, and recents are stored per user id.
  useEffect(() => {
    if (openOnFocus) setRecents(getRecentAccounts(user?.id, recentAccountsStore));
  }, [openOnFocus, user?.id, recentAccountsStore]);

  useEffect(() => {
    if (!canSearchDirectory) return;
    // The account directory is thousands strong, so an empty-query fetch is impractical. With no
    // query, show the accounts the user recently selected here so the dropdown is useful on focus.
    // Bump on every change, not just inside the debounce, or a request already in flight stays current.
    searchSeq.current += 1;
    if (searchQuery) {
      handleSearch(searchQuery);
      return;
    }
    handleSearch.cancel();
    setSearching(false);
    if (sectioned) setSearchedAccounts([]);
    else setSearchedAccounts(openOnFocus ? getRecentAccounts(user?.id, recentAccountsStore) : []);
  }, [searchQuery]);

  useEffect(() => () => handleSearch.cancel(), [handleSearch]);

  // Results replace the list under the recents while it is open; without this the listbox keeps
  // the offset it had and lands part-scrolled.
  useEffect(() => {
    if (listboxRef.current) listboxRef.current.scrollTop = 0;
  }, [searchResults]);

  const handleChange = (_e: React.SyntheticEvent<Element, Event>, value: Account | null) => {
    setSearchQuery(value?.name || '');
    if (value && openOnFocus) setRecents(pushRecentAccount(value, user?.id, recentAccountsStore));
    setSelectedAccount(value || undefined);
  };

  const handleInputChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    setSearchQuery(value || '');
  };

  // Anywhere on the bar opens the list, not just the icon. The input and the indicator keep their
  // own handling, so clicking either still toggles the dropdown shut.
  const handleBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.closest('.MuiAutocomplete-popupIndicator')) return;
    setOpen(true);
  };

  const renderSectionHeader = (params: { key: string; group: string; children?: React.ReactNode }) => (
    <li key={params.key}>
      <TextSpan
        size="xs"
        color="grey6"
        weight="medium"
        className="block px-4 pt-2 pb-1">
        {params.group}
      </TextSpan>
      <ul className="p-0">{params.children}</ul>
    </li>
  );

  return (
    <>
      {showHeader && <TextBlock className="text-md">Select the account associated with this user.</TextBlock>}
      <div
        id="user-account-selection-form"
        className={styles || ''}>
        <Autocomplete
          id="user-account-selection-input"
          data-testid="user-account-selection-input"
          data-cy="user-account-dropdown"
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          value={selectedValue}
          disabled={disabled}
          disableClearable
          loading={searching}
          openOnFocus={openOnFocus}
          className="w-full"
          onInputChange={handleInputChange}
          open={sectioned ? open : undefined}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          ListboxProps={{ ref: listboxRef, style: { maxHeight: sectioned ? 320 : 150 } }}
          onChange={handleChange}
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          options={options}
          getOptionLabel={(option: Account) => option?.name}
          size={size || 'small'}
          popupIcon={sectioned ? <SearchIcon fontSize="small" /> : undefined}
          // The server already narrowed the results; RECENT stays listed whatever the term is.
          filterOptions={sectioned ? (opts) => opts : undefined}
          groupBy={sectioned ? (option) => (recentIds.has(option.id) ? RECENT_GROUP : searchResultsGroup) : undefined}
          renderGroup={sectioned ? renderSectionHeader : undefined}
          sx={
            sectioned ? { ...styleOverride, '.MuiAutocomplete-popupIndicator': { transform: 'none' } } : styleOverride
          }
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{ ...params.InputProps, style: { fontSize: '.875rem' } }}
              label={showLabel ? label : ''}
              className={inputStyles || INPUT_STYLE}
              data-cy="account-input"
              onClick={sectioned ? handleBarClick : undefined}
              placeholder={showLabel ? '' : label}
            />
          )}
          renderOption={(props, option) => (
            <li
              {...props}
              data-cy={option.id}
              className={`${props['className' as keyof typeof props] || ''} text-sm14 py-1 pl-4`}
              key={`${option.id}-${option.name}`}>
              {option.name}
            </li>
          )}
        />
      </div>
    </>
  );
};
